"use client";

import { useState, useEffect } from "react";

interface Section {
  id: string;
  label: string;
}

interface SectionNavProps {
  sections: Section[];
}

export function SectionNav({ sections }: SectionNavProps) {
  const [active, setActive] = useState(sections[0]?.id || "");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActive(entry.target.id);
          }
        }
      },
      { rootMargin: "-80px 0px -60% 0px", threshold: 0.1 },
    );

    for (const section of sections) {
      const el = document.getElementById(section.id);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, [sections]);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 70;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  if (sections.length <= 1) return null;

  return (
    <div className="sticky top-[56px] z-30 -mx-5 mb-4 border-b border-gray-200 bg-white/95 backdrop-blur-sm">
      <nav className="scrollbar-none flex gap-0 overflow-x-auto px-5">
        {sections.map((s) => (
          <button
            key={s.id}
            onClick={() => scrollTo(s.id)}
            className={`shrink-0 border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${
              active === s.id
                ? "border-violet-600 text-violet-700"
                : "text-muted-foreground hover:text-foreground border-transparent"
            }`}
          >
            {s.label}
          </button>
        ))}
      </nav>
    </div>
  );
}
